<template>
    <div class="p-day-one__form">
        <control label="Disease or Accident" :icon-space="false">
            <select v-model="diseaseOrAccident">
                <option :value="null" hidden>Please select...</option>
                <option value="disease">Disease</option>
                <option value="accident">Accident</option>
            </select>
        </control>

        <control label="Disease Type" :icon-space="false" v-if="diseaseOrAccident === 'disease'">
            <select v-model="diseaseType">
                <option :value="null" hidden>Please select...</option>
                <option value="Asbestos Related Lung Cancer">Asbestos Related Lung Cancer</option>
                <option value="Bladder Cancer">Bladder Cancer</option>
                <option value="Kidney Cancer">Kidney Cancer</option>
                <option value="Laryngeal Cancer">Laryngeal Cancer</option>
                <option value="Leukemia">Leukemia</option>
                <option value="Liver Cancer">Liver Cancer</option>
                <option value="Mesothelioma">Mesothelioma</option>
                <option value="Nasal Cancer">Nasal Cancer</option>
                <option value="Skin Cancer">Skin Cancer</option>
                <option value="Other">Other</option>
                <option value="Other Cancer">Other Cancer</option>
            </select>
        </control>

        <control label="Accident Type" :icon-space="false" v-if="diseaseOrAccident === 'accident'">
            <select v-model="accidentType">
                <option :value="null" hidden>Please select...</option>
                <option value="Accident - EL">Accident - EL</option>
                <option value="Accident - PL">Accident - PL</option>
                <option value="Motor">Motor</option>
                <option value="Other">Other</option>
            </select>
        </control>

        <control label="Dependency or Estate Claim" :icon-space="false">
            <select v-model="estateOrDependency">
                <option :value="null" hidden>Please Select...</option>
                <option value="dependency">Dependency Claim</option>
                <option value="estate">Estate Claim</option>
            </select>
        </control>

        <control label="Number of Dependants" :icon-space="false" v-if="estateOrDependency=='dependency'">
            <select v-model="numberOfDependants">
                <option :value="null" hidden>Please Select...</option>
                <option :value="1">1 Dependent</option>
                <option :value="2">2 Dependents</option>
                <option :value="3">3 Dependents</option>
                <option :value="4">4 Dependents</option>
            </select>
        </control>

        <control label="Gender of Deceased" :icon-space="false">
            <select v-model="gender">
                <option :value="null" hidden>Please Select...</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
            </select>
        </control>

        <control label="Deceased's DOB" :icon-space="false">
            <date-input v-model="dateOfBirth" v-field-control:dob="dobRules"></date-input>
            <control-error :show="$error('dob')">
                The person must be born in the past
            </control-error>
        </control>

        <control label="Onset of Symptoms" :icon-space="false">
            <date-input v-model="onsetOfSymptoms" v-field-control:osos="oSoSRules"></date-input>
            <control-error :show="$error('osos')">
                The symptoms must be before the date of death
            </control-error>
        </control>

        <control label="Deceased's Date of Death" :icon-space="false">
            <date-input v-model="dateOfDeath" v-field-control:dod="dodRules"></date-input>
            <control-error :show="$error('dod')">
                The person must have died after they were born.
            </control-error>
        </control>

        <control label="Deceased's Age on Death" before-after="years-old" :icon-space="false">
            <input type="number" min="0" v-model="ageAtDeath">
        </control>

        <control label="Date of Assessment (or assumed)" :icon-space="false">
            <date-input v-model="assumedSettlement" v-field-control:assumed="assumedRules"></date-input>
            <control-error :show="$error('assumed')">
                The assessment date must be after death.
            </control-error>
        </control>

        <control label="Employed or Retired" :icon-space="false">
            <select v-model="employmentType">
                <option :value="null" hidden>Please Select...</option>
                <option value="employed">Employed</option>
                <option value="retired">Retired</option>
            </select>
        </control>

        <div v-if="employmentType=='employed'">
            <control label="Gross Annual Salary" before-after="pound" :icon-space="false">
                <input type="text" min="0" v-model="salary">
            </control>
            <salary-finder v-if="gender" :gender="gender" @update="updateSalary"></salary-finder>
        </div>
        <control label="Procedural Status" :icon-space="false">
            <select v-model="proceduralStatus">
                <option :value="null" hidden>Please Select...</option>
                <option value="pre-lit">Pre-Litigation</option>
                <option value="litigated">Litigated</option>
            </select>
        </control>
        <div v-if="showErrorMessage">
            <span class="a-form-inputs--error">You must complete all fields.</span>
        </div>
        Calculate the results:
        <div>
            <button class="a-button u-no-margin" @click="calculate">Calculate</button>
            <button class="a-button u-no-margin" @click="reset">Reset</button>
        </div>
    </div>
</template>

<script>
    import Control from 'components/form/Control.vue';
    import ControlError from 'components/form/control/Error.vue';
    import FormValidator from 'components/form/validator/mixin';
    import CurrencyFormat from 'mixins/currencyFormat';
    import DateInput from 'components/form/control/DateInput.vue';
    import SalaryFinder from 'pages/reserving/components/SalaryFinder';
    import {mapActions, mapGetters} from 'vuex';
    import moment from 'moment';

    export default {
        name: 'day-one-fatal',
        components: {Control, ControlError, FormValidator, DateInput, SalaryFinder},
        mixins: [
            FormValidator,
            CurrencyFormat
        ],
        mounted() {
            this.reset();
        },
        computed: {
            ...mapGetters('reserves/dayOneFatal', [
                'isValid',
                'formDirty',
            ]),
            ...mapGetters('windowWidth', ['theCurrentWidth']),
            diseaseOrAccident: {
                get() {
                    return this.getValue('diseaseOrAccident');
                },
                set(value) {
                    return this.updateValue({key: 'diseaseOrAccident', value});
                }
            },
            diseaseType: {
                get() {
                    return this.getValue('diseaseType');
                },
                set(value) {
                    this.updateValue({key: 'accidentType', value: null});
                    return this.updateValue({key: 'diseaseType', value});
                }
            },
            accidentType: {
                get() {
                    return this.getValue('accidentType');
                },
                set(value) {
                    this.updateValue({key: 'diseaseType', value: null});
                    return this.updateValue({key: 'accidentType', value});
                }
            },
            estateOrDependency: {
                get() {
                    return this.getValue('estateOrDependency');
                },
                set(value) {
                  console.log(value);
                    return this.updateValue({key: 'estateOrDependency', value});
                }
            },
            gender: {
                get() {
                    return this.getValue('gender');
                },
                set(value) {
                    return this.updateValue({key: 'gender', value});
                }
            },
            dateOfBirth: {
                get() {
                    return this.getValue('dateOfBirth');
                },
                set(value) {
                    return this.updateValue({key: 'dateOfBirth', value});
                }
            },
            dateOfDeath: {
                get() {
                    return this.getValue('dateOfDeath');
                },
                set(value) {
                    return this.updateValue({key: 'dateOfDeath', value});
                }
            },
            ageAtDeath: {
                get() {
                    return this.getValue('ageAtDeath');
                },
                set(value) {
                    return this.updateValue({key: 'ageAtDeath', value});
                }
            },
            assumedSettlement: {
                get() {
                    return this.getValue('assumedSettlement');
                },
                set(value) {
                    return this.updateValue({key: 'assumedSettlement', value});
                }
            },
            employmentType: {
                get() {
                    return this.getValue('employmentType');
                },
                set(value) {
                    return this.updateValue({key: 'employmentType', value});
                }
            },
            onsetOfSymptoms: {
                get() {
                    return this.getValue('onsetOfSymptoms');
                },
                set(value) {
                    return this.updateValue({key: 'onsetOfSymptoms', value});
                }
            },
            proceduralStatus: {
                get() {
                    return this.getValue('proceduralStatus');
                },
                set(value) {
                    return this.updateValue({key: 'proceduralStatus', value});
                }
            },
            numberOfDependants: {
                get() {
                    return this.getValue('numberOfDependants');
                },
                set(value) {
                    return this.updateValue({key: 'numberOfDependants', value});
                }
            },
            dobRules() {
                return {
                    type: "date",
                    rules: 'max:' + moment().format('DD/MM/YYYY')
                }
            },
            dodRules() {
                return {
                    type: "date",
                    rules: 'min:' + this.dateOfBirth
                }
            },
            assumedRules() {
                return {
                    type: "date",
                    rules: 'min:' + this.dateOfDeath
                }
            },
            oSoSRules() {
                return {
                    type: "date",
                    rules: 'max:' + this.dateOfDeath
                }
            },
            occupation() {
                return this.getValue('occupation');
            },
            salary: {
                get() {
                    return this.getValue('salary');
                },
                set(value) {
                    return this.updateValue({key: 'salary', value});
                }
            }
        },
        data() {
            return {
                showErrorMessage: false
            }
        },
        methods: {
            ...mapActions('reserves/dayOneFatal', [
                'updateValue',
                'performCalculation',
                'reset'
            ]),
            getValue(attr) {
                return this.$store.getters['reserves/dayOneFatal/' + attr];
            },
            calculate() {
                this.showErrorMessage = !this.isValid || !this.$valid();
                if (!this.showErrorMessage) {
                    // assign column one to a variable
                    let assignColumnOne = document.querySelector('.c-split-screen__column-one');
                    // get the bound elements of column one so we can target it's height
                    var elementBounding = assignColumnOne.getBoundingClientRect();
                    if(this.theCurrentWidth <= 1024) {
                        // scroll to the height of column one (plus the heights for passport and the header) which puts us at the top of column two
                        window.scrollTo(0, elementBounding.height + 108);
                    } else {
                        window.scrollTo(0, 0);
                    }

                    this.performCalculation();
                }
            },
            updateSalary(value) {
                return Promise.all([
                    this.updateValue({key: 'occupation', value: value.occupation}),
                    this.updateValue({key: 'salary', value: value.salary}),
                ]);
            }
        }
    }
</script>



// WEBPACK FOOTER //
// Form.vue?61db807e